import { Alert, Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { ErrorAlert, formatTs } from '../components';
import type { Integration } from '../integrationTypes';
import { CredentialsBlock } from './AccountEditor';
import { CardHeader, removeLines, SettingsSection } from './ConnectionCard';
import useCheckNow from './useCheckNow';
import FactStrip from './FactStrip';
import { useAction, useIntegrations } from './IntegrationsContext';
import { FLEET_SYNC_TYPES, statusSentence } from './integrationLabels';
import { CardSection } from './parts';
import RemoveDialog from './RemoveDialog';

/** A fleet connection (Quartix; plan §1 Verdicts "Fleet"): polling status, its facts, the on/off switch
 *  (`PATCH {enabled}`), the last poll error, credentials and settings. One Default account, no webhooks. */
const FleetCard = ({ connection }: { connection: Integration }) => {
    const { permissions, environment, api, replace, reload, noteRemoval, trackedSince } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const canEdit = permissions.edit && connection.editable;
    const fleet = connection.fleet;
    const account = connection.accounts[0];
    const toggling = useAction();
    const checkNow = useCheckNow(connection);
    const [removing, setRemoving] = React.useState(false);

    const setEnabled = async (enabled: boolean) => {
        const outcome = await toggling.run(() => api.setEnabled(connection.id, enabled));
        if (outcome.ok) {
            replace(outcome.value);
            enqueueSnackbar(enabled ? `${connection.name} switched on` : `${connection.name} switched off`, { variant: 'success' });
        }
    };

    return (
        <Paper
            variant="outlined"
            component="article"
            aria-labelledby={`connection-${connection.id}-title`}
            sx={{ p: { xs: 1.5, sm: 2 } }}
            data-testid={`connection-${connection.id}`}
        >
            <CardHeader
                connection={connection}
                actions={canEdit && connection.can_check ? checkNow.button : undefined}
                onRemove={canEdit && connection.can_remove ? () => setRemoving(true) : undefined}
            />
            <Typography variant="body2" data-testid="status-sentence" title={connection.status.at ? formatTs(connection.status.at) : undefined}>
                {statusSentence(connection.status, connection, trackedSince)}
            </Typography>
            {checkNow.result}
            {fleet && fleet.last_error && connection.status.verdict !== 'polling' && (
                <Alert severity={connection.status.verdict === 'failing' ? 'error' : 'warning'} sx={{ mt: 1.5 }} data-testid="fleet-error">
                    Last poll failed: {fleet.last_error}
                    {fleet.consecutive_failures > 1 ? ` (${fleet.consecutive_failures} failed polls in a row)` : ''}
                    {fleet.last_attempt_at ? `. Last attempt ${formatTs(fleet.last_attempt_at)}.` : ''}
                </Alert>
            )}
            <FactStrip facts={connection.facts} systemName={connection.name} />
            {fleet && (
                <Box data-testid="fleet-switch">
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={fleet.enabled}
                                disabled={!canEdit || toggling.busy}
                                onChange={(ev) => setEnabled(ev.target.checked)}
                            />
                        )}
                        label={<Typography variant="body2">Poll vehicle positions</Typography>}
                    />
                    {fleet.sync_type === 'push' && (
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
                            {FLEET_SYNC_TYPES.push}: Curated Planet does not poll this connection.
                        </Typography>
                    )}
                    {toggling.error ? <ErrorAlert error={toggling.error} /> : null}
                </Box>
            )}
            {account && (
                <CardSection title="Credentials" testId="single-account">
                    <CredentialsBlock connection={connection} account={account} canEdit={canEdit} />
                </CardSection>
            )}
            <SettingsSection connection={connection} canEdit={canEdit} />
            <RemoveDialog
                open={removing}
                title={`Remove ${connection.name}?`}
                lines={[
                    `Vehicle positions from ${connection.name} stop at once. Buses, trips and bookings stay.`,
                    ...removeLines(connection, environment).slice(1),
                ]}
                confirmation={connection.remove_confirmation}
                confirmLabel={`Type ${connection.remove_confirmation} to confirm`}
                onRemove={async (typed) => {
                    noteRemoval(connection.name, await api.remove(connection.id, typed));
                    enqueueSnackbar(`${connection.name} removed`, { variant: 'success' });
                    reload();
                }}
                onClose={() => setRemoving(false)}
            />
        </Paper>
    );
};

export default FleetCard;
